import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createTopicTypeApi, deleteTopicTypeApi, updateTopicTypeApi } from "../../../../api/support/common";
import { useModal } from "../../../../hooks";
import { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { isLoading } from "../../../../state/Spinner";

interface EditObject {
    idx: number;
    codeEntriesName: string;
}

export const useEditTopicTypeList = () => {
    const [editArr, setEditArr] = useState<EditObject[]>([]); // 편집중인 idx 배열
    const { handleModal } = useModal();
    const queryClient = useQueryClient();
    const setLoading = useSetAtom(isLoading);

    const onEdit = (idx: number, codeEntriesName: string) => {
        setEditArr(prev =>
            prev.map((item) => {
                if (item.idx === idx) {
                    item.codeEntriesName = codeEntriesName;
                }
                return item;
            })
        );
    }

    const addTopicType = (idx: number, codeEntriesName: string) => {
        if (idx > 0 && editArr.find((item) => item.idx === idx)) return;
        if (idx > 0) setEditArr(prev => [...prev, { idx, codeEntriesName }]);
        else {
            // 0부터 -1씩 감소하면서 idx가 같은게 없으면 추가
            let idx = 0;
            while (true) {
                if (!editArr.find((item) => item.idx === idx)) {
                    setEditArr(prev => [...prev, { idx, codeEntriesName }]);
                    break;
                }
                idx--;
            }
        }
    }

    const addMutation = useMutation({
        mutationFn: (data: { name: string, idx: number }) => createTopicTypeApi(data.name),
        onSuccess: (res: any, variables) => {
            if (res.code === "0000") {
                queryClient.invalidateQueries({ queryKey: ["readTopicType"] });
                setEditArr(prev => prev.filter((item) => item.idx !== variables.idx));
                handleModal({
                    isOpen: true,
                    contents: res.msg,
                    type: "alert",
                });
            }
        },
        onError: (error: AxiosError<{ msg?: string }>) => {
            console.log(error);
            if (error?.response?.data instanceof Blob) {
                error.response.data.text().then((value) => {
                    const obj = JSON.parse(value);
                    handleModal({
                        isOpen: true,
                        contents: obj?.msg,
                        type: "alert",
                    });
                });
            }
        }
    })

    const deleteMutation = useMutation({
        mutationFn: (data: number) => deleteTopicTypeApi(data),
        onSuccess: (res: any, variables) => {
            if (res.code === "0000") {
                queryClient.invalidateQueries({ queryKey: ["readTopicType"] });
                setEditArr(prev => prev.filter((item) => item.idx !== variables));
                /*handleModal({
                    isOpen: true,
                    innerJSX: <>{res.msg}</>,
                    title: "알림",
                });*/
            }
        },
        onError: (error: AxiosError<{ msg?: string }>) => {
            console.log(error);
            if (error?.response?.data instanceof Blob) {
                error.response.data.text().then((value) => {
                    const obj = JSON.parse(value);
                    /*handleModal({
                        isOpen: true,
                        innerJSX: <>{obj?.msg}</>,
                        title: "알림",
                    });*/
                });
            }
        }
    })

    const updateMutation = useMutation({
        mutationFn: (data: any) => updateTopicTypeApi(data.idx, data.codeEntriesName),
        onSuccess: (res: any, variables) => {
            if (res.code === "0000") {
                queryClient.invalidateQueries({ queryKey: ["readTopicType"] });
                setEditArr(prev => prev.filter((item) => item.idx !== variables.idx));
                /*handleModal({
                    isOpen: true,
                    innerJSX: <>{res.msg}</>,
                    title: "알림",
                });*/
            }
        },
        onError: (error: AxiosError<{ msg?: string }>) => {
            console.log(error);
            if (error?.response?.data instanceof Blob) {
                error.response.data.text().then((value) => {
                    const obj = JSON.parse(value);
                    /*handleModal({
                        isOpen: true,
                        innerJSX: <>{obj?.msg}</>,
                        title: "알림",
                    });*/
                });
            }
        }
    })

    const deleteTopicType = (idx: number) => {
        if (editArr.find((item) => item.idx === idx)) {
            setEditArr(prev => prev.filter((item) => item.idx !== idx));
        } else {
            setLoading(true);
            deleteMutation.mutate(idx);
        }
        setLoading(false);
    }

    const updateTopicType = (idx: number, codeEntriesName: string) => {
        const found = editArr.find((item) => item.idx === idx);
        if (found) {
            setLoading(true);
            if (idx <= 0) addMutation.mutate({ name: found.codeEntriesName, idx: idx });
            else updateMutation.mutate({ idx, codeEntriesName: found.codeEntriesName });
        } else {
            addTopicType(idx, codeEntriesName);
        }
        setLoading(false);
    }

    return {
        editArr, onEdit,
        addTopicType,
        deleteTopicType,
        updateTopicType
    }
}